import { CurrencyPipe, PercentPipe, DecimalPipe } from '@angular/common';
import { Pipe, PipeTransform, Inject, LOCALE_ID } from '@angular/core';

import { AnyValues } from '../interfaces/undetermined';

@Pipe({ name: 'arrayMap', })
export class ArrayMapPipe implements PipeTransform {

  // Se inyecta el LOCALE_ID al constructor siempre que se utilicen pipes de angular
  constructor(@Inject(LOCALE_ID) private locale) { }

  /* transform(): Pipe que devuelve la matriz recibida modificando por cada iteración propiedades seleccionadas de acuerdo a una categoría, y entregando una suma cundo la categoría es númerica
    arrayBase: Matriz que se desean modificar sus valores
    baseDetails: lista de propiedades a mostrar y su categoría.
    └ Formato: primera popiedad sin categoría "propiedad", en adelante "propiedad, categoría"
    └ Ejemplo: ['prop1', 'prop2, category']
    arrayReplace: Si se desea filtrar una propiedad de otro matriz teniendo como base una propiedad de un objeto de la matriz principal
    replaceDetails: propiedades para realizar el filtro
    └ Formato: "propiedad para unir objeto 1 y objeto 2", "propiedad objeto 2"
    └ Ejemplo: ['key1', 'key2']
    slice: Cantidad de decimales a mostrar
  */
  transform(
    arrayBase: AnyValues[],
    baseDetails: string[] = ['id_agente', 'num, number'],
    arrayReplace?: AnyValues[],
    replaceDetails?: string[],
    slice?: number | string,
    useIndexReplaced?: boolean,
  ): [string[][], number[]] {
    // ↓ Si se recibe una matriz para buscar por propiedad, pero no se definen las propiedades para buscar, se crean por defecto
    if (arrayReplace && !replaceDetails) { replaceDetails = ['id', 'nombre']; }
    // totalToReturn: Contiene la suma de cada propiedad seleccionada con categoría numérica
    const totalToReturn: number[] = [];
    // ↓ Devuelve un nuevo array, en posición 0 la matriz base con propiedades modificadas y en posición 1 la suma de las categorías númericas
    return [arrayBase.map((data: AnyValues) => {
      // arrToReturn: Array que guardará los valores que se retornarán por cada iteración
      const arrToReturn: string[] = [];
      // main: Guarda la propiedad principal de la matriz base
      let main: string = data[baseDetails[0]];
      // ↓ Extrae el valor de la coincidencia de propiedades cuando se recibe otra matriz
      if (arrayReplace) {
        // ↓ Verifica si se debe agregar el id además de la propiedad extraída
        if (useIndexReplaced) { arrToReturn.push(main); }
        main = arrayReplace.filter(value => value[replaceDetails[0]] === main)[0][replaceDetails[1]];
      }
      // ↓ Agrega la propiedad principal al array a retornar
      arrToReturn.push(main);
      // ↓ itera las propiedades solicitadas sin contar la primera para formatear según la categoría
      baseDetails.slice(1).forEach((value: string, index: number) => {
        // ↓ Verifica que no sea una propiedad nula
        if (value !== null) {
          // properly: Nombre de la propiedad del objeto
          const properly = value.split(', ')[0];
          // properlyType: Categoría
          const properlyType = value.split(', ')[1];
          // newValue: Guardará el valor modificado
          let newValue: any;
          switch (properlyType) {
            // ↓ Totaliza el valor y devuelve la propieda sin cambios
            case "NoFormatNumber":
              // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
              newValue = properly !== 'null' ? Number(data[properly]) : 0;
              // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
              !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
              // Agrega el nuevo valor al array a retornar
              arrToReturn.push(Number(newValue).toString());
              break;
            // ↓ Devuelve un valor porcentual
            case "percentage":
              // ↓ Verifica si la propiedad es 'N/A' para devolver el mismo texto, en caso contrario devuelve un número
              if (data[properly] !== 'N/A') {
                // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
                newValue = properly !== 'null' ? Number(data[properly]) : 0;
                // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
                !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
                // Agrega el nuevo valor con formato porcentual al array a retornar
                arrToReturn.push(new DecimalPipe(this.locale).transform(newValue, '1.0-' + (slice || '2')) + "%");
              } else { arrToReturn.push(data[properly]); }
              break;
            // ↓ Convierte número a formato peso colombiano con separador de miles y totaliza
            case "currency":
              // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
              newValue = properly !== 'null' ? Number(data[properly]) : 0;
              // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
              !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
              // Agrega el nuevo valor con formato peso colombiano al array a retornar
              arrToReturn.push(new CurrencyPipe(this.locale).transform(newValue, 'COP', 'symbol', '1.0-' + (slice || '2')));
              break;
            // ↓ Convierte número a formato peso colombiano con separador de miles y totaliza
            case "currency-null":
              // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
              newValue = properly !== 'null' ? (data[properly] !== "" ? Number(data[properly]) : "N/A") : 0;
              // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
              !totalToReturn[index] ? totalToReturn[index] = (isNaN(+newValue) ? 0 : +newValue) : totalToReturn[index] += (isNaN(+newValue) ? 0 : +newValue);
              // Agrega el nuevo valor con formato peso colombiano al array a retornar
              arrToReturn.push(newValue !== "N/A" ? new CurrencyPipe(this.locale).transform(newValue, 'COP', 'symbol', '1.0-' + (slice || '2')) : newValue);
              break;
            // ↓ Devuelve un valor porcentual multiplicado por 100
            case "percent":
              // ↓ Verifica si la propiedad es 'N/A' para devolver el mismo texto, en caso contrario devuelve un número
              if (data[properly] !== 'N/A') {
                // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
                newValue = properly !== 'null' ? Number(data[properly]) : 0;
                // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
                !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
                // Agrega el nuevo valor con formato porcentual al array a retornar
                arrToReturn.push(new PercentPipe(this.locale).transform(newValue, '1.0-' + (slice || '2')));
              } else { arrToReturn.push(data[properly]); }
              break;
            // ↓ Convierte cadena a formato numérico con separador de miles y totaliza
            case "number":
              // ↓ Verifica si la propiedad es 'null', entonces devuelve 0
              newValue = properly !== 'null' ? Number(data[properly]) : 0;
              // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
              !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
              // Agrega el nuevo valor con formato miles al array a retornar
              arrToReturn.push(new DecimalPipe(this.locale).transform(newValue));
              break;
            // ↓ Devuelve el mismo valor HH:mm:ss pero totaliza en segundos
            case "time":
              // ↓ Convierte HH:mm:ss a segundos
              newValue = Number(data[properly].split(':').reduce((pre: number, cur: number) => (60 * pre) + +cur));
              // ↑ +(variable) convierte a numero
              // ↓ Si no existe total en la posición actual de la propiedad, la crea, si existe suma
              !totalToReturn[index] ? totalToReturn[index] = newValue : totalToReturn[index] += newValue;
              // Agrega el mismmo valor al array a retornar
              arrToReturn.push(data[properly]);
              break;
            // ↓ Agrega un campo de texto al retorno
            case "text": arrToReturn.push(data[properly]); break;
            default: arrToReturn.push(`${data[properly]} ${properlyType}`); break;
          }
        }
      });
      // Devuelve la matriz con las propiedades modificadas
      return arrToReturn;
    }), totalToReturn];
  }

}
